import { Component, ElementRef, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import SelectColumnType from '@revolist/revogrid-column-select';
import { AuthService } from 'src/shared/services/auth.service';
import { FileService } from 'src/shared/services/file.service';
import { ResponseService } from 'src/shared/services/response-service.service';
import { SchoolProgramService } from 'src/shared/services/school-program.service';
import { ScoringService } from 'src/shared/services/scoring.service';
import { TranslateService } from 'src/shared/services/translate.service';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import { validateFiles } from '../../../shared/utils/file';
import {
  allowRevoGridFilterInputTyping,
  commitRevoGridEdit,
  createSingleSelectEditor,
  isEmptyValue,
  selectCellTemplate,
  syncRevoGridSource,
} from '../../../shared/utils/revo-grid';

/** One student per grid row; each score type i adds `value_i` (score or predicate) and `comment_i`. */
interface ScoreRow {
  activity_participant_id: number;
  student_name: string;
  class_name: string;
  /** Saved values per score type (same order as scoreRange), for the fields the grid does not edit. */
  saved: { activity_score_range_id: any; activity_score_id: any; score: any; predicate: any; comment: string }[];
  [prop: string]: any;
}

const ROW_SIZE = 40;

// Column widths (px). Name and class stay fixed; each score group shares what is left of the grid's width.
const STUDENT_COLUMN = 240;
const CLASS_COLUMN = 100;
const ROW_NUMBER_COLUMN = 50;
const SCROLLBAR = 16;
const VALUE_COLUMN = { min: 140, max: 220 };
const COMMENT_COLUMN_MIN = 200;

@Component({
  selector: 'app-scoring',
  templateUrl: './scoring.component.html',
  styleUrl: './scoring.component.scss'
})
export class ScoringComponent implements OnInit, OnDestroy {

  @ViewChild('uploadTemplate', {static: true}) public modalUploadTemplate: NgbModalRef;
  @ViewChild('scoreGrid', { read: ElementRef }) gridRef?: ElementRef<HTMLElement>;

  // The grid only exists once scores load, so watch its wrapper whenever it appears
  @ViewChild('gridWrap', { read: ElementRef }) set gridWrap(ref: ElementRef<HTMLElement> | undefined) {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (!ref) return;
    this.resizeObserver = new ResizeObserver(([entry]) => {
      const width = Math.floor(entry.contentRect.width);
      if (Math.abs(width - this.gridWidth) < 2) return;
      this.gridWidth = width;
      this.zone.run(() => this.buildColumns());
    });
    this.resizeObserver.observe(ref.nativeElement);
  }

  loading = {
    legend: false,
    student: false,
  }
  saving = false;
  loadExcel = false;
  previewData = [];
  isValid = false;
  hasData = false;  

  subs: Subscription;
  paramForm: FormGroup;
  scoreRange: any;
  participants: any;
  dataStudentScore: any;
  usingScoreRange = false;
  showTableStudent = false;
  showTableScore = false;
  academicYearVal: any = null;
  schoolLevelVal: any = null;
  schoolProgramVal: any = null;
  schoolProgramCategoryVal: any = null;
  schoolProgramActivityVal: any = null;
  subscription: any = {};
  warningText: any;

  // Score grid
  rows: ScoreRow[] = [];
  columns: any[] = [];
  columnTypes = { select: new SelectColumnType() };
  clipboardConfig = { rangeFill: true };
  readonly rowSize = ROW_SIZE;
  onGridKeydown = allowRevoGridFilterInputTyping;
  private invalidCells = new Map<ScoreRow, Set<string>>();
  private resizeObserver?: ResizeObserver;
  private gridWidth = 0;

  supervisorLocationAccess: any;

  constructor(
    private scoringService: ScoringService,
    private spService: SchoolProgramService,
    private translate: TranslateService,
    private rpSer: ResponseService,
    public auth: AuthService,
    private fileService: FileService,
    private modalService: NgbModal,
    private zone: NgZone,
  ) { }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  /**
   * Splits the width left after the fixed columns evenly between score groups. In each group the value column
   * takes a third (kept between its min and max so the number and its predicate stay close) and the comment the
   * rest. Below the minimums the grid scrolls sideways instead.
   */
  private scoreColumnSizes(): { value: number; comment: number } {
    const groups = Math.max(1, this.scoreRange?.length || 0);
    const available = this.gridWidth - STUDENT_COLUMN - CLASS_COLUMN - ROW_NUMBER_COLUMN - SCROLLBAR;
    const perGroup = Math.max(0, available) / groups;
    const value = Math.min(VALUE_COLUMN.max, Math.max(VALUE_COLUMN.min, Math.floor(perGroup / 3)));
    return { value, comment: Math.max(COMMENT_COLUMN_MIN, Math.floor(perGroup - value)) };
  }

  @ViewChild('uploadTemplate') uploadTemplate: TemplateRef<any>;

  ngOnInit(): void {
    this.auth.loadPermissions(true);
    this.translate.load('scoring', 'extracurricular');
    this.initForm();
  }

  initForm() {
    this.paramForm = new FormGroup({
      'academic_year': new FormControl(null),
      'period_id': new FormControl(null),
      'school_location_id': new FormControl(null),
      'school_level_id': new FormControl(null),
      'school_program_id': new FormControl(null),
      'school_program_category_id': new FormControl(null),
      'school_program_activity_id': new FormControl(null),
      'year_level_id': new FormControl(null)
    });
  }

  get canEdit(): boolean {
    return !!this.auth.permissions['SCHOOL_PROGRAM_SCORING_EDIT'];
  }

  get changedCount(): number {
    return this.rows.filter((row) => this.isRowChanged(row)).length;
  }

  /** A value as compared against the saved one: empty is null, scores are numbers, text is trimmed. */
  private comparable(value: any): any {
    if (isEmptyValue(value)) return null;
    return this.usingScoreRange ? this.parseScore(value) : String(value).trim();
  }

  /** Changed only when a value differs from what was loaded; touching or re-pasting the same value is not a change. */
  private isRowChanged(row: ScoreRow): boolean {
    return this.scoreRange.some((_: any, i: number) => {
      const saved = row.saved[i];
      return this.comparable(row[this.valueProp(i)]) !== this.comparable(this.usingScoreRange ? saved.score : saved.predicate)
        || String(row[this.commentProp(i)] ?? '').trim() !== String(saved.comment ?? '').trim();
    });
  }

  get invalidCount(): number {
    return [...this.invalidCells.values()].reduce((total, props) => total + props.size, 0);
  }

  // Tall enough for every row (plus the two header rows) up to ~13 students, then the grid scrolls
  get gridHeight(): number {
    return Math.min(640, Math.max(240, this.rows.length * ROW_SIZE + 120));
  }

  private t(key: string): string {
    return this.translate.data[key] || key;
  }

  private valueProp(index: number): string {
    return `value_${index}`;
  }

  private commentProp(index: number): string {
    return `comment_${index}`;
  }

  /** Lowest min_score and highest max_score of a score type; null ends when not set. */
  scoreBounds(range: any): { min: number | null; max: number | null } {
    const mins = range.score_detail.map((d: any) => d.min_score).filter((v: any) => v !== null && v !== undefined).map(Number);
    const maxs = range.score_detail.map((d: any) => d.max_score).filter((v: any) => v !== null && v !== undefined).map(Number);
    return { min: mins.length ? Math.min(...mins) : null, max: maxs.length ? Math.max(...maxs) : null };
  }

  /** The predicate whose min–max a score falls in, shown next to the score (not saved). */
  private predicateForScore(range: any, value: any): string | null {
    if (isEmptyValue(value) || isNaN(+value)) return null;
    const score = +value;
    const detail = range.score_detail.find((d: any) =>
      (d.min_score === null || score >= +d.min_score) && (d.max_score === null || score <= +d.max_score));
    return detail?.predicate ?? null;
  }

  /** Typed or pasted score as a number; "85,5" reads as 85.5. Text that is not a number is kept as is. */
  private parseScore(value: any): any {
    if (isEmptyValue(value)) return null;
    if (typeof value === 'number') return value;
    const text = String(value).trim().replace(/^(-?\d+),(\d+)$/, '$1.$2');
    return text !== '' && !isNaN(+text) ? +text : String(value).trim();
  }

  /** Pasted predicate matched to the option's own spelling ("a" → "A"); unknown text is kept as is. */
  private parsePredicate(range: any, value: any): any {
    if (isEmptyValue(value)) return null;
    const text = String(value).trim().toLowerCase();
    const detail = range.score_detail.find((d: any) => String(d.predicate).trim().toLowerCase() === text);
    return detail ? detail.predicate : String(value).trim();
  }

  private isValueInvalid(range: any, value: any): boolean {
    if (isEmptyValue(value)) return false;
    if (!this.usingScoreRange) {
      return !range.score_detail.some((d: any) => d.predicate === value);
    }
    if (typeof value !== 'number') return true;
    const { min, max } = this.scoreBounds(range);
    return (min !== null && value < min) || (max !== null && value > max);
  }

  private invalidProps(row: ScoreRow): string[] {
    return this.scoreRange
      .map((range: any, i: number) => (this.isValueInvalid(range, row[this.valueProp(i)]) ? this.valueProp(i) : null))
      .filter(Boolean);
  }

  private buildColumns(): void {
    const sizes = this.scoreColumnSizes();
    // The value column opens each score group; `col-group-start` draws the heavier divider before it
    const editable = (prop: string) => ({
      columnProperties: () => ({ class: { 'col-group-start': true } }),
      cellProperties: ({ model }: any) => ({
        class: { 'col-group-start': true, 'revo-cell-invalid': !!this.invalidCells.get(model)?.has(prop) },
      }),
    });

    const valueColumn = (range: any, i: number) => {
      const prop = this.valueProp(i);
      if (!this.usingScoreRange) {
        return {
          prop,
          name: this.t('PREDICATE'),
          size: sizes.value,
          sortable: true,
          // type to search: predicates starting with the typed text, first match highlighted
          editor: createSingleSelectEditor({
            source: () => range.score_detail.map((d: any) => ({
              value: d.predicate,
              label: d.description ? `${d.predicate} · ${d.description}` : d.predicate,
            })),
            searchText: (option) => String(option.value ?? ''),
            emptyText: () => this.t('No matching predicate'),
          }),
          cellTemplate: selectCellTemplate((model) => model[prop] ?? '', () => !this.canEdit, range.score_name),
          ...editable(prop),
        };
      }
      const { min, max } = this.scoreBounds(range);
      return {
        prop,
        name: min !== null && max !== null ? `${this.t('SCORE')} (${min}–${max})` : this.t('SCORE'),
        size: sizes.value,
        sortable: true,
        // score on the left, the predicate it falls in on the right
        cellTemplate: (h: any, props: any) => {
          const value = props.model[prop];
          const predicate = this.isValueInvalid(range, value) ? null : this.predicateForScore(range, value);
          return h('div', { class: 'revo-cell-with-icon score-cell' }, [
            h('span', { class: 'revo-cell-text' }, value ?? ''),
            predicate ? h('span', { class: 'score-cell__predicate' }, predicate) : null,
          ]);
        },
        ...editable(prop),
      };
    };

    this.columns = [
      { prop: 'student_name', name: this.t('STUDENT_NAME'), size: STUDENT_COLUMN, pin: 'colPinStart', readonly: true, sortable: true, filter: 'string' },
      { prop: 'class_name', name: this.t('Class'), size: CLASS_COLUMN, pin: 'colPinStart', readonly: true, sortable: true, filter: 'string' },
      ...this.scoreRange.map((range: any, i: number) => ({
        name: range.score_name,
        children: [
          valueColumn(range, i),
          { prop: this.commentProp(i), name: this.t('Comment'), size: sizes.comment },
        ],
      })),
    ];
  }

  private toRow(student: any): ScoreRow {
    const row: ScoreRow = {
      activity_participant_id: student.activity_participant_id,
      student_name: student.student_name,
      class_name: student.class_name ?? '',
      saved: [],
    };
    this.scoreRange.forEach((range: any, i: number) => {
      const detail = (student.score_detail ?? []).find((d: any) => d.activity_score_range_id === range.activity_score_range_id);
      const saved = {
        activity_score_range_id: range.activity_score_range_id,
        activity_score_id: detail?.activity_score_id || null,
        score: detail?.score ?? null,
        predicate: detail?.predicate || null,
        comment: detail?.comment || '',
      };
      row.saved.push(saved);
      row[this.valueProp(i)] = this.usingScoreRange ? saved.score : saved.predicate;
      row[this.commentProp(i)] = saved.comment;
    });
    return row;
  }

  onAfterEdit(event: CustomEvent<any>): void {
    const detail = event.detail;
    // a paste or fill reports many rows in models/data; a single edit reports model/prop
    const edited: ScoreRow[] = detail?.models && detail?.data
      ? Object.keys(detail.data).map((index) => detail.models[index]).filter(Boolean)
      : detail?.model ? [detail.model] : [];
    edited.forEach((row) => {
      this.scoreRange.forEach((range: any, i: number) => {
        const prop = this.valueProp(i);
        row[prop] = this.usingScoreRange ? this.parseScore(row[prop]) : this.parsePredicate(range, row[prop]);
      });
      const props = this.invalidProps(row);
      if (props.length) {
        this.invalidCells.set(row, new Set(props));
      } else {
        this.invalidCells.delete(row);
      }
    });
    if (edited.length) {
      this.refreshGrid();
    }
  }

  private refreshGrid(): void {
    (this.gridRef?.nativeElement as any)?.refresh?.('all')?.catch?.(() => undefined);
  }

  onChangeAcademicYear(item: any) {
    this.academicYearVal = item ? item : null;
    this.paramForm.controls['academic_year'].markAsTouched();
    this.paramForm.controls['academic_year'].setValue(item ? item.academic_year : null);
    this.paramForm.controls['period_id'].markAsTouched();
    this.paramForm.controls['period_id'].setValue(item ? +item.period_id : null);

    this.paramForm.controls['school_location_id'].setValue(null);
    this.paramForm.controls['year_level_id'].setValue(null);
    this.paramForm.controls['school_program_id'].setValue(null);
    this.paramForm.controls['school_program_category_id'].setValue(null);
    this.paramForm.controls['school_program_activity_id'].setValue(null);

    if (this.academicYearVal) {
      this.getSupervisorLocationAccess();
    }
  }

  onChangeSchoolLevel(item: any) {
    this.schoolLevelVal = item ? item : null;
    this.paramForm.controls['school_location_id'].markAsTouched();
    this.paramForm.controls['school_location_id'].setValue(item ? item.school_location_id : null);

    this.paramForm.controls['school_level_id'].markAsTouched();
    this.paramForm.controls['school_level_id'].setValue(item ? item.school_level_id : null);

    this.paramForm.controls['year_level_id'].setValue(null);
    this.paramForm.controls['school_program_id'].setValue(null);
    this.paramForm.controls['school_program_category_id'].setValue(null);
    this.paramForm.controls['school_program_activity_id'].setValue(null);
  }

  onChangeSchoolProgram(item: any) {
    this.schoolProgramVal = item ? item : null;
    this.paramForm.controls['school_program_id'].markAsTouched();
    this.paramForm.controls['school_program_id'].setValue(item ? item.school_program_id : null);

    this.paramForm.controls['school_program_category_id'].setValue(null);
    this.paramForm.controls['school_program_activity_id'].setValue(null);
  }

  onChangeSchoolProgramCategory(item: any) {
    this.schoolProgramCategoryVal = item ? item : null;
    this.paramForm.controls['school_program_category_id'].markAsTouched();
    this.paramForm.controls['school_program_category_id'].setValue(item ? item.school_program_category_id : null);

    this.paramForm.controls['school_program_activity_id'].setValue(null);
  }

  onChangeSchoolProgramActivity(item: any) {
    this.schoolProgramActivityVal = item ? item : null;
    this.paramForm.controls['school_program_activity_id'].markAsTouched();
    this.paramForm.controls['school_program_activity_id'].setValue(item ? item.school_program_activity_id : null);
    if (item) {
      this.getScoreDetail();
    }
  }

  clearData() {
    this.showTableScore = false;
    this.showTableStudent = false;
    this.warningText = null;
    this.scoreRange = [];
    this.clearStudentScore();
  }

  hasNonNullScores(data: any) {
    if (!data || data.length === 0) {
      return false;
    }
    for (const activity of data) {
      for (const detail of activity.score_detail) {
        if (detail.min_score !== null || detail.max_score !== null) {
          return true;
        }
      }
    }
    return false;
  }

  getSupervisorLocationAccess() {
    if (this.subscription.get_supervisor_location_access) {
      this.subscription.get_supervisor_location_access.unsubscribe();
    }
    this.subscription.get_supervisor_location_access = this.spService.getSupervisorLocationAccess({
      academic_year: this.paramForm.controls['academic_year'].value,
    }).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.supervisorLocationAccess = res.data;
        } else {
          this.rpSer.showAddError(res.message);
        }
      },
      error: (err: any) => {
        this.rpSer.showAddError();
      }
    });
  }

  getScoreDetail() {
    if (this.subscription.get_score_detail) {
      this.subscription.get_score_detail.unsubscribe();
    }
    this.warningText = null;
    this.loading.legend = true;
    this.showTableScore = false;
    this.usingScoreRange = false;
    this.scoreRange = [];
    this.subscription.get_score_detail = this.scoringService.getScoreDetail(this.paramForm.controls['school_program_activity_id'].value, this.paramForm.value).subscribe({
      next: (res: any) => {
        if (res.status) {
          this.scoreRange = res.data ? res.data : [];
          if (this.hasNonNullScores(this.scoreRange)) {
            this.usingScoreRange = true;
          }
          this.buildColumns();
          this.getStudentScore();
          this.showTableScore = this.scoreRange.length > 0;
        } else {
          this.rpSer.showAddError(res.message ? res.message : 'An unexpected error occurred. Please check your connection and try again.');
        }
        this.loading.legend = false;
      },
      error: (err: any) => {
        this.rpSer.showAddError();
        this.loading.legend = false;
      }
    });
  }

  clearStudentScore() {
    this.rows = [];
    this.invalidCells.clear();
  }

  getStudentScore() {
    if (this.subscription.get_student_score) {
      this.subscription.get_student_score.unsubscribe();
    }
    this.loading.student = true;
    this.showTableStudent = false;
    this.clearStudentScore();
    this.subscription.get_student_score = this.scoringService.getStudentScore(this.paramForm.controls['school_program_activity_id'].value, this.paramForm.controls['period_id'].value).subscribe({
      next: (res: any) => {
        this.clearStudentScore();
        if (res.status) {
          this.dataStudentScore = res.data ?? [];
          const seenParticipantIds = new Set<number>();
          this.rows = this.dataStudentScore
            .filter((student: any) => {
              if (seenParticipantIds.has(student.activity_participant_id)) {
                return false;
              }
              seenParticipantIds.add(student.activity_participant_id);
              return true;
            })
            .map((student: any) => this.toRow(student));
          // flag stored values that no longer fit the score guide
          this.rows.forEach((row) => {
            const props = this.invalidProps(row);
            if (props.length) {
              this.invalidCells.set(row, new Set(props));
            }
          });
          this.showTableStudent = true;
        } else {
          this.showTableStudent = false;
          this.warningText = res.message;
        }
        this.loading.student = false;
      },
      error: (err: any) => {
        this.rpSer.showAddError();
        this.loading.student = false;
      }
    })
  }

  get excelButtonCondition(): boolean {
    return this.academicYearVal && this.schoolLevelVal && this.schoolProgramVal && this.schoolProgramCategoryVal && this.schoolProgramActivityVal;
  }

  async onSaveScore() {
    if (this.saving) {
      return;
    }
    commitRevoGridEdit(this.gridRef?.nativeElement);
    this.rows = await syncRevoGridSource(this.gridRef?.nativeElement, this.rows);
    if (this.invalidCount > 0) {
      Swal.fire({
        title: 'Check the highlighted cells',
        text: this.usingScoreRange
          ? 'Some scores are not numbers or are outside the score range. Fix them before saving.'
          : 'Some predicates are not in the list. Pick one from the list before saving.',
        icon: 'warning'
      });
      return;
    }

    // Same shape the reactive form used to send: one entry per student, one score_detail per score type
    const payload = {
      student_score: this.rows.map((row) => ({
        student_name: row.student_name,
        class_name: row.class_name,
        activity_participant_id: row.activity_participant_id,
        score_detail: this.scoreRange.map((range: any, i: number) => ({
          score: this.usingScoreRange ? row[this.valueProp(i)] ?? null : row.saved[i].score,
          activity_score_range_id: range.activity_score_range_id,
          activity_score_id: row.saved[i].activity_score_id,
          predicate: this.usingScoreRange ? row.saved[i].predicate : row[this.valueProp(i)] ?? null,
          comment: row[this.commentProp(i)] ?? '',
        })),
      })),
    };

    Swal.fire({
      title: 'Confirmation',
      text: 'Do you want to save the score?',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: 'Yes'
    }).then((result) => {
      if (result.isConfirmed) {
        this.saving = true;
        this.scoringService.saveStudentsScore(this.paramForm.controls['school_program_activity_id'].value, payload).subscribe({
          next: (res: any) => {
            this.saving = false;
            if (res.status) {
              this.rpSer.showAddSuccess('Activity scores has been saved successfully.');
              this.getStudentScore();
            } else {
              this.rpSer.showAddError(res.message ? res.message : 'An unexpected error occurred. Please check your connection and try again.');
            }
          },
          error: (err: any) => {
            this.saving = false;
            this.rpSer.showAddError();
          }
        })
      }
    });
  }

  onDownloadTemplate() {
    this.scoringService.scoringTemplate(this.paramForm.controls['school_program_activity_id'].value, this.paramForm.value).subscribe({
      next: (response: Blob) => {
        if(response) {
          const blobUrl = URL.createObjectURL(response as any);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = 'scoring_template.xlsx';
          link.click();
        } else {
          this.rpSer.showAddError(response ? response as any : 'An unexpected error occurred. Please check your connection and try again.');
        }
      },
      error: (error) => {
        this.rpSer.showAddError(error ? error : 'An unexpected error occurred. Please check your connection and try again.');
      }
    })
  }
  
  async uploadExcel(item: any) {
    this.loadExcel = true;

    const result: any[] = [];
  
    if(item.addedFiles.length === 0) {
      this.rpSer.showAddError('No file selected');
      this.loadExcel = false;
      return;
    }
    
    // Validate File Type
    if(
      !await validateFiles(
        item.addedFiles, 
        ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel',
        ],
        this.translate,
      )
    ) {
      item.addedFiles = [];
      this.rpSer.showAddError('Invalid file type');
      this.loadExcel = false;
      return;
    }

    const file = item.addedFiles[0];
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);

    reader.onload = async (e: any) => {
      const binaryStr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(binaryStr, { type: 'binary' });
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];
      const range = XLSX.utils.decode_range(ws['!ref']);
  
      const  data = XLSX.utils.sheet_to_json(ws, {
          range: 1,
          header:[
            'activity_participant_id',
            'student_name', 
            'score_range_id',
            'score_name',
            range?.e?.c >= 6 ? 'score' : undefined,
            'predicate',
            'comment',
          ].filter(Boolean),
          defval: '',
        });
      
      result.push(...data);
    };


    reader.onloadend = async () => {
      this.excelCreateData(result)
        .then(() => {
          this.loadExcel = false;
          this.getStudentScore();
          this.modalService.dismissAll();
        });
    };

    this.loadExcel = false;
  }

  excelCreateData(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      const param = {
        school_program_activity_id: this.paramForm.controls['school_program_activity_id'].value,
        data: data
      }
      this.scoringService.uploadScore(param)
        .subscribe((response: any) => {
          if (response.status) {
            this.previewData = response.data.data;
            this.isValid = response.data.valid;
            this.hasData = true;
            this.rpSer.showAddSuccess(response.message ? response.message : 'Success');
            resolve(true);
          } else {
            this.rpSer.showAddError(response.message ? response.message : 'An unexpected error occurred. Please check your connection and try again.');
            reject();
          }
        }, (error: any) => {
          this.rpSer.showAddError(error ? error : 'An unexpected error occurred. Please check your connection and try again.');
          reject();
        });
    });
  }

  async openWindow() { 
    this.modalService.open(this.uploadTemplate, {
      ariaLabelledBy: 'modal-basic-title',
      keyboard: false,
      size: 'lg'
    });
  }

  onUploadExcel() {  
    this.openWindow();
  }
}
